import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, ClipboardList, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ReadOnlyBanner, DetailHeader, Section, DL, inr, fmtDate, fmtDateTime, safe, arr } from "./shared";

const PAY_CFG = {
  paid: { cls: "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25", label: "Paid" },
  partially_paid: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Partial" },
  unpaid: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Unpaid" },
  refunded: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Refunded" },
};
const PayChip = ({ s }) => {
  const c = PAY_CFG[s] || { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: s || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
};

export default function AdminInvoiceDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/ops/invoices/${id}`);
        if (live) setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [id]);

  if (loading) return <div className="p-10 text-center text-[#667085]">Loading invoice…</div>;
  if (!data) return <div className="p-10 text-center text-[#e63946]">Invoice not found.</div>;

  const inv = data.invoice;
  const totals = inv.totals || {};
  const grandTotal = Number(totals.invoice_total || inv.total || 0);
  const paySum = inv.payments_summary || {};
  const paid = Number(paySum.total_paid || 0);
  const outstanding = grandTotal - paid;

  const garage = data.garage || {};
  const customer = data.customer || {};
  const cs = inv.customer_snapshot || {};
  const vs = inv.vehicle_snapshot || {};
  const linked = data.linked || {};
  const payments = arr(data.payments);
  const items = arr(inv.items);
  const parts = items.filter((i) => i.kind === "part" || i.kind === "spare");
  const labour = items.filter((i) => i.kind === "labour" || i.kind === "labor" || i.kind === "service");
  const others = items.filter((i) => !["part", "spare", "labour", "labor", "service"].includes(i.kind));

  return (
    <div className="space-y-4 max-w-6xl" data-testid="admin-inv-detail">
      <DetailHeader
        crumb="Back to Invoices"
        backTo="/admin/invoices"
        title={inv.number || inv.invoice_number || `INV ${(inv.id || "").slice(0, 8)}`}
        subtitle={`${garage.name || "—"} · ${fmtDate(inv.invoice_date || inv.issued_at || inv.created_at)}`}
        right={
          <>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold ${inv.invoice_type === "gst" ? "bg-[#457b9d]/10 text-[#1d3557] border-[#457b9d]/25" : "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]"}`}>{(inv.invoice_type || "").toUpperCase() || "—"}</span>
            <PayChip s={inv.payment_status} />
            <button onClick={() => window.print()} className="h-8 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5">
              <Printer size={13} /> Print
            </button>
          </>
        }
      />

      <ReadOnlyBanner />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Invoice Total</div>
          <div className="text-[20px] font-bold text-[#1d3557] font-mono mt-0.5">{inr(grandTotal)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Amount Received</div>
          <div className="text-[20px] font-bold text-[#12B76A] font-mono mt-0.5">{inr(paid)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Outstanding</div>
          <div className={`text-[20px] font-bold font-mono mt-0.5 ${outstanding > 0 ? "text-[#e63946]" : "text-[#12B76A]"}`}>{inr(outstanding)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Payment Status</div>
          <div className="mt-1"><PayChip s={inv.payment_status} /></div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {garage.id && <Link to={`/admin/garages/${garage.id}`} className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"><Building2 size={13} /> Open Garage</Link>}
        {linked.job_card && <Link to={`/admin/job-cards/${linked.job_card.id}`} className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#457b9d] hover:border-[#457b9d] inline-flex items-center gap-1.5"><ClipboardList size={13} /> Job Card {linked.job_card.number}</Link>}
        {linked.estimate && <Link to={`/admin/estimates/${linked.estimate.id}`} className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#457b9d] hover:border-[#457b9d] inline-flex items-center gap-1.5"><FileText size={13} /> Estimate {linked.estimate.number}</Link>}
      </div>

      <Section title="Customer" testId="inv-customer-section">
        <DL cols={3} pairs={[
          ["Name", customer.name || cs.name],
          ["Mobile", customer.phone || cs.phone],
          ["Email", customer.email || cs.email],
          ["Address", cs.address || customer.address],
          ["City / State", `${cs.city || customer.city || ""} ${cs.state || customer.state || ""}`.trim()],
          ["GSTIN", cs.gstin],
        ]} />
      </Section>

      <Section title="Vehicle" testId="inv-vehicle-section">
        <DL cols={3} pairs={[
          ["Registration", vs.reg_number],
          ["Make", vs.make],
          ["Model", vs.model],
          ["Variant", vs.variant],
          ["VIN", vs.vin],
          ["Fuel Type", vs.fuel_type],
        ]} />
      </Section>

      <Section title="Line Items" testId="inv-items-section">
        {items.length === 0 && <div className="text-[13px] text-[#667085]">No line items.</div>}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-[12px] border border-[#E4E7EC] rounded-md">
              <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-3 py-2">Kind</th>
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2">Part #</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Discount</th>
                  <th className="px-3 py-2 text-right">GST%</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1FAEE]">
                {items.map((it, i) => (
                  <tr key={it.id || i}>
                    <td className="px-3 py-2 capitalize">{safe(it.kind)}</td>
                    <td className="px-3 py-2 font-semibold text-[#1d3557]">{safe(it.name)}</td>
                    <td className="px-3 py-2 font-mono text-[#667085]">{safe(it.part_number)}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(it.qty || 0)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(it.rate || it.unit_price)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(it.discount)}</td>
                    <td className="px-3 py-2 text-right font-mono">{it.gst_rate || 0}%</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{inr(it.total || it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Summary" testId="inv-summary-section">
        <DL cols={2} pairs={[
          ["Parts", inr(totals.parts_total)],
          ["Labour", inr(totals.labour_total || totals.labor_total)],
          ["Other Charges", inr(totals.charges_total)],
          ["Discount", inr(totals.discount_amount || inv.discount)],
          ["Taxable", inr(totals.taxable || inv.subtotal)],
          ["CGST", inr(totals.cgst)],
          ["SGST", inr(totals.sgst)],
          ["IGST", inr(totals.igst)],
          ["Round-off", inr(totals.round_off)],
          ["Invoice Total", inr(grandTotal)],
          ["Advance Adjusted", inr(paySum.advance_adjusted || 0)],
          ["Amount Received", inr(paid)],
          ["Outstanding", inr(outstanding)],
        ]} />
      </Section>

      <Section title={`Payments (${payments.length})`} testId="inv-payments-section">
        {payments.length === 0 && <div className="text-[13px] text-[#667085]">No payments recorded yet.</div>}
        {payments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-[12px] border border-[#E4E7EC] rounded-md">
              <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Received By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1FAEE]">
                {payments.map((p, i) => (
                  <tr key={p.id || i}>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(p.payment_date || p.created_at)}</td>
                    <td className="px-3 py-2 capitalize">{safe(p.mode || p.payment_mode)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-[#12B76A]">{inr(p.amount)}</td>
                    <td className="px-3 py-2 font-mono text-[#667085]">{safe(p.reference || p.payment_reference)}</td>
                    <td className="px-3 py-2">{safe(p.received_by_name || p.received_by)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {inv.notes && (
        <Section title="Notes" testId="inv-notes-section">
          <div className="text-[13px] text-[#1d3557] whitespace-pre-line">{safe(inv.notes)}</div>
        </Section>
      )}
    </div>
  );
}
